import {
  VehicleRegistry,
  type Unsubscribe,
  type Vehicle,
  type VehicleClass,
} from '@hgc/core';
import type { LinkState } from '@hgc/link';

/**
 * 界面上认识一辆车所需的**全部**信息。
 *
 * 刻意不是 `Vehicle` 本身：那是运行时的可变对象，把它交给 React 组件等于把
 * "状态到底什么时候变了"这个问题的答案又还给每个调用点。这里是不可变摘要，
 * 引用稳定（内容不变就复用旧对象），因此可以直接进 `useSyncExternalStore`。
 *
 * 摘要也刻意**不含**遥测数值：那些每帧都变，放进列表会让机体选择器跟着遥测一起重渲染。
 * 遥测走 `useVehicleState()`（只订阅当前焦点那一辆）。
 */
export interface VehicleSummary {
  readonly id: string;
  readonly systemId: number;
  readonly componentId: number;
  /** 一句话身份，例如 `1:1 · 多旋翼` */
  readonly label: string;
  readonly vehicleClass: VehicleClass;
  readonly typeName: string | null;
  /** 是不是飞控。心跳还没到且组件号不是 1 时为 null（未知） */
  readonly isAutopilot: boolean | null;
  readonly modeName: string | null;
  readonly armed: boolean;
  readonly online: boolean;
  readonly linkState: LinkState;
  readonly primary: boolean;
}

const CLASS_LABELS: Readonly<Record<VehicleClass, string>> = {
  copter: '多旋翼',
  plane: '固定翼',
  rover: '无人车',
  sub: '潜航器',
  other: '其它',
  unknown: '机型未知',
};

export const EMPTY_VEHICLE_LIST: readonly VehicleSummary[] = Object.freeze([]);

function summarize(vehicle: Vehicle, primary: boolean): VehicleSummary {
  const state = vehicle.state;
  return Object.freeze({
    id: vehicle.id,
    systemId: vehicle.systemId,
    componentId: vehicle.componentId,
    label: `${vehicle.systemId}:${vehicle.componentId} · ${CLASS_LABELS[state.vehicleClass]}`,
    vehicleClass: state.vehicleClass,
    typeName: state.typeName,
    isAutopilot: state.isAutopilot,
    modeName: state.modeName,
    armed: state.armed,
    online: state.online,
    linkState: state.linkState,
    primary,
  });
}

function sameSummary(a: VehicleSummary, b: VehicleSummary): boolean {
  return (
    a.id === b.id &&
    a.label === b.label &&
    a.vehicleClass === b.vehicleClass &&
    a.typeName === b.typeName &&
    a.isAutopilot === b.isAutopilot &&
    a.modeName === b.modeName &&
    a.armed === b.armed &&
    a.online === b.online &&
    a.linkState === b.linkState &&
    a.primary === b.primary
  );
}

/**
 * 车辆列表的投影：把注册表里那些**可变对象**翻译成不可变摘要，并且只在摘要真的变了时才通知。
 *
 * 这一层存在的理由只有一个：`registry.list()` 每次调用都返回**新数组**，
 * 直接交给 `useSyncExternalStore` 会让 React 认为"每次都变了"，进入无限重渲染。
 * 因此缓存必须由运行时持有，而不是每个组件各自 `useMemo`（那又会退化成多份真相）。
 */
export class VehicleRoster {
  private readonly registry: VehicleRegistry;
  private readonly sink: (list: readonly VehicleSummary[]) => void;
  private readonly offs: Unsubscribe[] = [];
  private current: readonly VehicleSummary[] = EMPTY_VEHICLE_LIST;

  constructor(registry: VehicleRegistry, sink: (list: readonly VehicleSummary[]) => void) {
    this.registry = registry;
    this.sink = sink;
    this.offs.push(
      registry.events.on('added', () => this.sync()),
      registry.events.on('removed', () => this.sync()),
      // 摘要里的解锁状态与模式来自每一帧的状态快照，所以必须跟着 state 走。
      // 代价可接受：摘要字段极少变化，`sameSummary` 会把绝大多数帧挡在通知之外。
      registry.events.on('state', () => this.sync()),
      registry.events.on('primaryChanged', () => this.sync()),
    );
    this.sync();
  }

  dispose(): void {
    for (const off of this.offs) off();
    this.offs.length = 0;
  }

  private sync(): void {
    const primaryId = this.registry.primaryId;
    const next = this.registry.list().map((vehicle) => summarize(vehicle, vehicle.id === primaryId));

    if (next.length === this.current.length) {
      let identical = true;
      for (let index = 0; index < next.length; index += 1) {
        const before = this.current[index];
        const after = next[index];
        if (before === undefined || after === undefined || !sameSummary(before, after)) {
          identical = false;
          break;
        }
      }
      if (identical) return;
    }

    this.current = Object.freeze(next);
    this.sink(this.current);
  }
}
